import type { TFunction } from "i18next";
import type { TeamPublicApplicationAction } from "./teamPublicDetailsModal.contract";

interface ApplicationActionModalCopy {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
}

interface ResolveApplicationActionModalCopyParams {
  readonly action: TeamPublicApplicationAction;
  readonly t: TFunction;
  readonly language: string;
}

function isSwedishLanguage(language: string): boolean {
  const normalizedLanguage = language.toLocaleLowerCase();
  return normalizedLanguage === "se" || normalizedLanguage.startsWith("sv");
}

export function formatApplicantPossessiveName({
  language,
  name,
}: {
  readonly language: string;
  readonly name: string;
}): string {
  const trimmedName = name.trim();
  if (trimmedName.length === 0) return trimmedName;

  if (isSwedishLanguage(language)) {
    return /[sxz]$/i.test(trimmedName) ? trimmedName : `${trimmedName}s`;
  }

  return /s$/i.test(trimmedName) ? `${trimmedName}'` : `${trimmedName}'s`;
}

/**
 * Resolves localized modal copy for application actions in the schedule modal.
 */
export function resolveApplicationActionModalCopy(
  params: ResolveApplicationActionModalCopyParams,
): ApplicationActionModalCopy {
  const applicantPossessiveName = formatApplicantPossessiveName({
    language: params.language,
    name: params.action.applicantName,
  });

  switch (params.action.type) {
    case "assign":
      return {
        title: params.t("Schedule.TeamPublicConfirmAssignTitle"),
        body: params.t("Schedule.TeamPublicConfirmAssignBody", {
          applicantPossessiveName,
        }),
        confirmLabel: params.t("Schedule.TeamPublicAssign"),
      };
    case "withdrawOffer":
      return {
        title: params.t("GigsCompany.WithdrawOffer"),
        body: params.t("GigsCompany.AreYouSureWithdrawOffer"),
        confirmLabel: params.t("GigsCompany.CancelOffer"),
      };
    case "decline":
      return {
        title: params.t("Schedule.TeamPublicConfirmDeclineTitle"),
        body: params.t("Schedule.TeamPublicConfirmDeclineBody", {
          applicantPossessiveName,
        }),
        confirmLabel: params.t("Schedule.TeamPublicDecline"),
      };
  }
}
